// Configuration constants
const CONFIG = {
	apiEndpoints: {
		CALLBACK_SUBMIT: gadgetAppUrl + '/callbackSubmit'
	},
	selectors: {
		modal: 'callbackModal',
		callbackType: 'callbackType',
		childOption: 'childOption',
		childOptionsGroup: 'childOptionsGroup',
		callbackForm: '.callback-form',
		contactForm: '.callback-contact-form',
		contactFormElement: 'contactForm',
		agreementCheckbox: 'agreementCheckbox'
	},
	charLimits: {
		firstName: 50,
		lastName: 50,
		message: 80
	},
	messages: {
		agreementRequired: 'Please check the agreement checkbox to continue.'
	}
};

/**
 * CallbackManager - Main module class
 */
class CallbackManager {
	constructor() {
		this.options = window.callback_options || [];
		this.customerData = window.customerData || { isLoggedIn: false };
		this.elements = {};
		this.init();
	}

	/**
	 * Initialize the module
	 */
	init() {
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => this.setup());
		} else {
			this.setup();
		}
	}

	/**
	 * Setup DOM elements and event listeners
	 */
	setup() {
		this.cacheElements();
		this.bindDropdownEvents();
		this.bindFormEvents();
		this.bindModalEvents();
	}

	/**
	 * Cache DOM elements for reuse
	 */
	cacheElements() {
		this.elements = {
			modal: document.getElementById(CONFIG.selectors.modal),
			callbackType: document.getElementById(CONFIG.selectors.callbackType),
			childOption: document.getElementById(CONFIG.selectors.childOption),
			childOptionsGroup: document.getElementById(CONFIG.selectors.childOptionsGroup),
			callbackForm: document.querySelector(CONFIG.selectors.callbackForm),
			contactForm: document.querySelector(CONFIG.selectors.contactForm),
			contactFormElement: document.getElementById(CONFIG.selectors.contactFormElement),
			agreementCheckbox: document.getElementById(CONFIG.selectors.agreementCheckbox)
		};
	}

	/**
	 * Bind dropdown event listeners
	 */
	bindDropdownEvents() {
		const { callbackType, childOption } = this.elements;

		if (callbackType) {
			callbackType.addEventListener('change', (e) => this.handleCallbackTypeChange(e));
		}

		if (childOption) {
			childOption.addEventListener('change', (e) => this.handleChildOptionChange(e));
		}
	}

	/**
	 * Bind modal event listeners
	 * Handles modal open/close via event delegation
	 */
	bindModalEvents() {
		const { modal } = this.elements;

		// Bind trigger button to open modal
		const triggerButton = document.querySelector('[data-action="open-callback-modal"]');
		if (triggerButton) {
			triggerButton.addEventListener('click', () => this.showModal());
		}

		// Bind close button inside modal
		const closeButton = document.querySelector('[data-action="close-callback-modal"]');
		if (closeButton) {
			closeButton.addEventListener('click', () => this.closeModal(CONFIG.selectors.modal));
		}

		if (modal) {
			// Close modal when clicking on the overlay background
			modal.addEventListener('click', (e) => {
				// Only close if clicking directly on the modal overlay, not on its children
				if (e.target === modal) {
					this.closeModal(CONFIG.selectors.modal);
				}
			});
		}
	}

	/**
	 * Handle callback type dropdown change
	 */
	handleCallbackTypeChange(event) {
		const selectedIndex = parseInt(event.target.value);
		
		if (isNaN(selectedIndex) || selectedIndex < 0 || !this.options[selectedIndex]) {
			this.hideChildOptions();
			return;
		}

		const selectedOption = this.options[selectedIndex];
		const childOptions = selectedOption.child_options || [];

		if (childOptions.length > 0) {
			this.populateChildOptions(childOptions);
			this.showChildOptions();
		} else {
			this.hideChildOptions();
			this.processCallback();
		}
	}

	/**
	 * Handle child option dropdown change
	 */
	handleChildOptionChange(event) {
		if (event.target.value !== '') {
			this.removePlaceholderOption(event.target);
			this.processCallback();
		}
	}

	/**
	 * Populate child options dropdown
	 */
	populateChildOptions(childOptions) {
		const { childOption } = this.elements;
		if (!childOption) return;

		// Clear existing options
		childOption.length = 0;

		// Add placeholder option
		const placeholder = document.createElement('option');
		placeholder.value = '';
		placeholder.textContent = 'Select an option';
		childOption.appendChild(placeholder);

		// Add child options
		childOptions.forEach((option, index) => {
			const optionElement = document.createElement('option');
			optionElement.value = index;
			optionElement.textContent = option.title || option.label || option.name || option.value || '';
			childOption.appendChild(optionElement);
		});
	}

	/**
	 * Show child options group
	 */
	showChildOptions() {
		if (this.elements.childOptionsGroup) {
			this.elements.childOptionsGroup.classList.remove('child-options-hidden');
		}
	}

	/**
	 * Hide child options group
	 */
	hideChildOptions() {
		if (this.elements.childOptionsGroup) {
			this.elements.childOptionsGroup.classList.add('child-options-hidden');
		}
	}

	/**
	 * Remove placeholder option after selection
	 */
	removePlaceholderOption(selectElement) {
		const placeholder = selectElement.querySelector('option[value=""]');
		if (placeholder) placeholder.remove();
	}

	/**
	 * Process callback selection and determine action
	 */
	processCallback() {
		const { callbackType, childOption, childOptionsGroup } = this.elements;
		
		if (!callbackType || !callbackType.value) return;

		const selectedIndex = parseInt(callbackType.value);
		const selectedCallback = this.options[selectedIndex];
		
		if (!selectedCallback) return;

		let redirectUrl = selectedCallback.redirect_url || '';

		// Check if child option is visible and selected
		const isChildVisible = childOptionsGroup && !childOptionsGroup.classList.contains('child-options-hidden');
		if (isChildVisible && childOption && childOption.value) {
			const childIndex = parseInt(childOption.value);
			const selectedChild = selectedCallback.child_options?.[childIndex];
			if (selectedChild) {
				redirectUrl = selectedChild.redirect_url || '';
			}
		}

		// Redirect or show contact form
		if (redirectUrl.trim()) {
			window.location.href = redirectUrl;
		} else {
			this.showContactForm();
		}
	}

	/**
	 * Show the callback modal
	 */
	showModal() {
		const { modal, callbackType, childOption, childOptionsGroup, callbackForm, contactForm } = this.elements;
		
		if (!modal) {
			return;
		}

		modal.classList.add('active');

		// Reset form state
		if (callbackType) {
			callbackType.selectedIndex = 0;
		}
		if (childOption) {
			childOption.selectedIndex = 0;
		}
		if (childOptionsGroup) {
			childOptionsGroup.classList.add('child-options-hidden');
		}
		if (callbackForm) {
			callbackForm.style.display = 'block';
		}
		if (contactForm && contactForm.classList.contains('active')) {
			contactForm.classList.remove('active');
		}

		this.resetContactFormView();

		// Re-apply prefilled customer data on modal reopen
		this.prefillCustomerData();
	}

	/**
	 * Close modal by ID
	 */
	closeModal(modalId) {
		const modal = document.getElementById(modalId);
		if (modal) {
			modal.classList.remove('active');
		}
	}

	/**
	 * Show contact form
	 */
	showContactForm() {
		const { callbackForm, contactForm } = this.elements;
		
		if (callbackForm) {
			callbackForm.style.display = 'none';
		}
		if (contactForm) {
			contactForm.classList.add('active');
		}
	}

	/**
	 * Bind contact form events
	 */
	bindFormEvents() {
		const { contactFormElement } = this.elements;
		if (!contactFormElement) {
			return;
		}

		contactFormElement.addEventListener('submit', (e) => this.handleFormSubmit(e));
		this.setupCharacterCounters();
		this.prefillCustomerData();
	}

	/**
	 * Pre-fill form fields with customer data if logged in
	 */
	prefillCustomerData() {
		if (!this.customerData || !this.customerData.isLoggedIn) {
			return;
		}

		const fields = [
			{ id: 'email', value: this.customerData.email },
			{ id: 'firstName', value: this.customerData.firstName },
			{ id: 'lastName', value: this.customerData.lastName }
		];

		fields.forEach(field => {
			const element = document.getElementById(field.id);
			if (element && field.value) {
				element.value = field.value;
				// Update character counter if applicable
				if (CONFIG.charLimits[field.id]) {
					this.updateCharacterCount(element, CONFIG.charLimits[field.id]);
				}
			}
		});
	}

	/**
	 * Handle form submission
	 */
	async handleFormSubmit(event) {
		event.preventDefault();
		
		const { agreementCheckbox } = this.elements;

		// Validate agreement checkbox
		if (!agreementCheckbox || !agreementCheckbox.checked) {
			if (agreementCheckbox) {
				agreementCheckbox.focus();
			}
			this.showErrorMessage(CONFIG.messages.agreementRequired);
			return;
		}

		// Get submit button and store original text
		const submitButton = event.target.querySelector('button[type="submit"]');
		const originalButtonText = submitButton ? submitButton.textContent : '';

		try {
			// Disable button and change text to "Submitting..."
			if (submitButton) {
				submitButton.disabled = true;
				submitButton.textContent = 'Submitting...';
			}

			// Collect form data
			const formData = new FormData(event.target);
			const data = Object.fromEntries(formData);
			const selectedReasonOption = event.target.querySelector('#reason option:checked');
			const reasonCode = data.reason || '';
			const reasonLabel = selectedReasonOption
				? selectedReasonOption.textContent.trim()
				: reasonCode;

			const submitData = {
				email: data.email,
				first_name: data.firstName,
				last_name: data.lastName,
				User_Type: "Customer",
				Created_Date: new Date().toISOString(),
				Created_on_eShop: this.customerData.isLoggedIn,
				Created_on_eShopSpecified: this.customerData.isLoggedIn,
				eShop_IDSpecified: this.customerData.isLoggedIn,
				eShop_ConnectorID: this.customerData.shopifyId ? String(this.customerData.shopifyId) : null,
				reason: reasonLabel,
				reason_code: reasonCode,
				reason_label: reasonLabel,
				website_status: null,
				customer_note: data.message,
				customer_id: null,
				Name: null,
				Name_2: null,
				Contact: null,
				Address: null,
				Address_2: null,
				Phone_No: data.phone,
				Fax_No: null,
				country_id: data.country,
				Post_Code: null,
				City: null,
				County: null,
				B2C_Customer: true,
				language_id: null
			}

			const response = await fetch(CONFIG.apiEndpoints.CALLBACK_SUBMIT, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json'
				},
				body: JSON.stringify({
					formData: JSON.stringify(submitData)
				})
			});

			if (!response.ok) {
				throw new Error(`Callback request failed: ${response.status}`);
			}

			this.showFormSuccess();

			// Restore button state
			if (submitButton) {
				submitButton.disabled = false;
				submitButton.textContent = originalButtonText;
			}
		} catch (error) {
			console.error('Form submission error:', error);
			this.showErrorMessage('An error occurred while submitting your request. Please review and try again.');
			
			// Restore button state on error
			if (submitButton) {
				submitButton.disabled = false;
				submitButton.textContent = originalButtonText;
			}
		}
	}


	/**
	 * Setup character counters for inputs
	 */
	setupCharacterCounters() {
		const fields = [
			{ id: 'firstName', maxLength: CONFIG.charLimits.firstName },
			{ id: 'lastName', maxLength: CONFIG.charLimits.lastName },
			{ id: 'message', maxLength: CONFIG.charLimits.message }
		];

		fields.forEach(field => {
			const element = document.getElementById(field.id);
			if (element) {
				element.addEventListener('input', () => {
					this.updateCharacterCount(element, field.maxLength);
				});
			}
		});
	}

	/**
	 * Update character count display
	 */
	updateCharacterCount(element, maxLength) {
		const remaining = maxLength - element.value.length;
		const charCount = element.parentNode?.querySelector('.char-count');
		
		if (charCount) {
			charCount.textContent = `Characters available: ${remaining}`;
		}
	}

	/**
	 * Reset all character counters
	 */
	resetCharacterCounters() {
		Object.entries(CONFIG.charLimits).forEach(([fieldName, maxLength]) => {
			const element = document.getElementById(fieldName);
			if (element) {
				const charCount = element.parentNode?.querySelector('.char-count');
				if (charCount) {
					charCount.textContent = `Characters available: ${maxLength}`;
				}
			}
		});
	}

	/**
	 * Show success message in place of the contact form
	 */
	showFormSuccess() {
		const { contactForm, modal } = this.elements;
		if (!contactForm) return;

		const body = contactForm.querySelector('.callback-contact-form__body');
		const success = contactForm.querySelector('.callback-contact-form__success');
		const modalTitle = modal?.querySelector('#callbackModalTitle');

		if (body) body.hidden = true;
		if (success) success.hidden = false;
		if (modalTitle) modalTitle.hidden = true;
	}

	/**
	 * Reset contact form view to show the form again
	 */
	resetContactFormView() {
		const { contactForm, contactFormElement, modal } = this.elements;
		if (!contactForm) return;

		const body = contactForm.querySelector('.callback-contact-form__body');
		const success = contactForm.querySelector('.callback-contact-form__success');
		const modalTitle = modal?.querySelector('#callbackModalTitle');

		if (body) body.hidden = false;
		if (success) success.hidden = true;
		if (modalTitle) modalTitle.hidden = false;

		if (contactFormElement) {
			contactFormElement.reset();
			this.resetCharacterCounters();
		}
	}

	/**
	 * Create and display a notification banner
	 * @param {'success' | 'error'} type - The type of notification
	 * @param {string} title - The notification title
	 * @param {string} message - The notification message
	 */
	createNotificationBanner(type, title, message) {
		const iconSymbol = type === 'success' ? '✓' : '!';

		const banner = document.createElement('div');
		banner.className = `notification-banner ${type}`;

		const content = document.createElement('div');
		content.className = 'notification-content';

		const icon = document.createElement('div');
		icon.className = 'notification-icon';
		icon.textContent = iconSymbol;

		const heading = document.createElement('h3');
		heading.textContent = title;

		const paragraph = document.createElement('p');
		paragraph.textContent = message;

		const closeBtn = document.createElement('button');
		closeBtn.className = 'close-btn';
		closeBtn.textContent = '×';
		closeBtn.addEventListener('click', () => banner.remove());

		content.appendChild(icon);
		content.appendChild(heading);
		content.appendChild(paragraph);
		content.appendChild(closeBtn);
		banner.appendChild(content);
		document.body.appendChild(banner);

		// Auto-remove after 10 seconds
		setTimeout(() => {
			if (banner.parentElement) {
				banner.remove();
			}
		}, 10000);
	}

	/**
	 * Show error message to user
	 * @param {string} message - The error message to display
	 */
	showErrorMessage(message) {
		this.createNotificationBanner('error', 'Error!', message);
	}
}

// Initialize the module
const callbackManager = new CallbackManager();

// Expose public API to global scope
window.showCallbackModal = () => callbackManager.showModal();
window.closeModal = (modalId) => callbackManager.closeModal(modalId);
window.handleCallbackSubmit = () => callbackManager.processCallback();
